import { useCallback, useState } from "react"
import { useQueryWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import { GetLinkableAssetsDocument } from "@hydrologiq/gql-experience/graphql"

export interface LinkableAsset {
  id: string
  name: string
  category: string | null
  class: string | null
  kind: string | null
  status: string | null
}

export interface UseLinkableAssetsReturn {
  fetchLinkableAssets: (params: {
    existingParent?: string
    assetClass?: string
    system?: string
  }) => void
  linkableAssets: LinkableAsset[]
  loading: boolean
  error: string | undefined
}

/**
 * Hook to fetch assets eligible for linking as children.
 *
 * - If `system` is provided: returns same-class assets from that system
 * - If no `system`: returns same-class unassigned assets (no system_node)
 */
export function useLinkableAssets(): UseLinkableAssetsReturn {
  const [variables, setVariables] = useState<{
    assetClass?: string
    existingParent?: string
    system?: string
  } | null>(null)

  const { data, loading, error } = useQueryWithEndpoint(
    GetLinkableAssetsDocument,
    {
      variables: variables ?? {},
      skip: !variables,
    },
    "primary",
  )

  const fetchLinkableAssets = useCallback(
    (params: { existingParent?: string; assetClass?: string; system?: string }) => {
      setVariables(params)
    },
    [],
  )

  const linkableAssets = (data?.getLinkableAssets ?? []) as LinkableAsset[]

  return {
    fetchLinkableAssets,
    linkableAssets,
    loading,
    error: error?.message,
  }
}
